/**
 * Theme tokens — Surface Layer (/01)
 *
 * Re-exports allowed token keys + validation helpers shared by the
 * theme provider and the Settings tearsheet.
 */

import {
  ALLOWED_CARBON_TOKEN_KEYS,
  ALLOWED_SURFACE_TOKEN_KEYS,
  type ThemeTokenOverrides,
  type SurfaceTokenOverrides,
} from '@contracts/theme.contract';

export {
  ALLOWED_CARBON_TOKEN_KEYS,
  ALLOWED_SURFACE_TOKEN_KEYS,
};

/** Length-typed Carbon keys (radius, max-width). Validated and clamped. */
const LENGTH_KEYS: ReadonlyArray<keyof ThemeTokenOverrides> = [
  'cds-button-border-radius',
  'cds-modal-border-radius',
  'cds-card-border-radius',
] as const;

const COLOR_KEYS: ReadonlyArray<keyof ThemeTokenOverrides> = [
  'cds-background',
  'cds-layer-01',
  'cds-layer-02',
  'cds-layer-03',
  'cds-interactive',
  'cds-button-primary',
  'cds-button-primary-hover',
  'cds-border-subtle',
  'cds-border-subtle-01',
  'cds-border-strong',
  'cds-border-interactive',
  'cds-text-primary',
  'cds-text-secondary',
  'cds-field-01',
  'cds-support-error',
  'cds-support-success',
  'cds-support-warning',
  'cds-support-info',
  'cds-link-primary',
  'cds-icon-primary',
  'cds-focus',
] as const;

const FONT_KEYS: ReadonlyArray<keyof ThemeTokenOverrides> = [
  'cds-font-family-sans',
  'cds-font-family-mono',
] as const;

export function isLengthKey(key: string): key is keyof ThemeTokenOverrides {
  return LENGTH_KEYS.includes(key as keyof ThemeTokenOverrides);
}

export function isColorKey(key: string): key is keyof ThemeTokenOverrides {
  return COLOR_KEYS.includes(key as keyof ThemeTokenOverrides);
}

export function isFontKey(key: string): key is keyof ThemeTokenOverrides {
  return FONT_KEYS.includes(key as keyof ThemeTokenOverrides);
}

/** Parses a CSS length string `Npx` or `Nrem`; returns pixels or null. */
export function parseLengthToPx(value: string): number | null {
  const trimmed = value.trim();
  const pxMatch = trimmed.match(/^(\d+(?:\.\d+)?)px$/);
  if (pxMatch?.[1]) return Number.parseFloat(pxMatch[1]);
  const remMatch = trimmed.match(/^(\d+(?:\.\d+)?)rem$/);
  if (remMatch?.[1]) return Number.parseFloat(remMatch[1]) * 16;
  if (/^\d+(?:\.\d+)?$/.test(trimmed)) return Number.parseFloat(trimmed);
  return null;
}

/** Rough CSS color validator. Accepts hex, rgb(a), hsl(a), named, var(). */
export function isLikelyCssColor(value: string): boolean {
  const v = value.trim();
  if (/^#[0-9a-fA-F]{3,8}$/.test(v)) return true;
  if (/^rgba?\(/i.test(v) && v.endsWith(')')) return true;
  if (/^hsla?\(/i.test(v) && v.endsWith(')')) return true;
  if (/^var\(--[\w-]+(?:,[^)]*)?\)$/.test(v)) return true;
  // Bare color names allowed (white, black, etc.)
  if (/^[a-zA-Z]+$/.test(v)) return true;
  return false;
}

/** Reject anything that could be a JS URL or expression. */
export function isSafeTokenValue(value: string): boolean {
  const v = value.toLowerCase();
  if (v.includes('javascript:')) return false;
  if (v.includes('expression(')) return false;
  if (v.includes('url(')) return false; // disallow url() in custom-property overrides
  if (v.includes('<')) return false;
  return true;
}

export type AnyTokenOverrides = ThemeTokenOverrides & SurfaceTokenOverrides;
